import { useEffect, useState } from 'react';
import { getOrgProfile, type OrganizationProfile } from '@/lib/api/org-profile';
import { currentTenantOrNull } from '@/lib/tenants';

// Story 076 — keep document.title in sync with the loaded org name
// so the browser tab + history match the rendered branding. The
// static title in index.html covers the no-JS / pre-React-paint
// case (Google OAuth verification crawler etc.); this hook handles
// post-mount updates for any tenant.
function syncDocumentTitle(name: string | null | undefined): void {
  const trimmed = name?.trim();
  if (trimmed && trimmed.length > 0 && document.title !== trimmed) {
    document.title = trimmed;
  }
}

// Public read of the current tenant's organization profile (name,
// description, logo URL). Returns null on tenants without a profile
// (public tenant — the white-label flag is off) and on fetch failure
// (so call sites can fall through to default branding).
//
// No caching — landing pages are visited rarely and the profile is
// small. Add a SWR/react-query layer if this becomes hot.
export function useOrgProfile(): {
  profile: OrganizationProfile | null;
  loading: boolean;
} {
  const isWhiteLabel = currentTenantOrNull()?.isWhiteLabel === true;
  const [profile, setProfile] = useState<OrganizationProfile | null>(null);
  const [loading, setLoading] = useState(isWhiteLabel);

  useEffect(() => {
    if (!isWhiteLabel) return;
    let cancelled = false;
    getOrgProfile()
      .then((p) => {
        if (!cancelled) {
          setProfile(p);
          syncDocumentTitle(p.name);
        }
      })
      .catch(() => {
        /* no-op — fall through to default branding */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isWhiteLabel]);

  return { profile, loading };
}
